import type { ReactNode } from 'react';

/**
 * The page header bar — one definition for every view that has one, so the bar height/padding,
 * the "‹ Back" control and the title size cannot drift apart again.
 *
 * The markup is the one DashboardView (Sensor Dashboard) uses, which is the reference style.
 * Omit `onBack` on views you cannot go back from (the home/chat view).
 */
interface PageHeaderProps {
  title: string;
  /** Omit when the view has nothing to go back to (e.g. the home view). */
  onBack?: () => void;
  /** Label next to the chevron. */
  backLabel?: string;
  /** Right-aligned actions (buttons, dropdowns) for this page. */
  right?: ReactNode;
}

export default function PageHeader({ title, onBack, backLabel = 'Back', right }: PageHeaderProps) {
  return (
    <div className="draggable flex h-12 shrink-0 items-center justify-between px-4 border-b dark:border-claude-darkBorder border-claude-border">
      <div className="non-draggable flex min-w-0 items-center gap-3">
        {onBack && (
          <button
            onClick={onBack}
            className="h-8 px-3 inline-flex items-center gap-1.5 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {backLabel}
          </button>
        )}
        <h1 className="truncate text-lg font-semibold dark:text-claude-darkText text-claude-text">{title}</h1>
      </div>
      {right ? <div className="ml-4 non-draggable flex items-center gap-2">{right}</div> : null}
    </div>
  );
}
